<script setup lang="ts">
import Card from "~/components/Common/Cards/Card.vue";
import Tag from "~/components/Common/Tag.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import SvgButton from "~/components/Common/Buttons/SvgButton.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import { useAlerts } from "~/composables/alerts/useAlerts";
import { AlertScope, AlertStatus } from "~/utils/enums/alerts.enums";
import type { IAlert } from "~/utils/interfaces/alerts-interfaces";

const props = withDefaults(
  defineProps<{
    alert: IAlert;
    link?: string;
    scope?: AlertScope;
  }>(),
  { scope: AlertScope.GLOBAL },
);

const emit = defineEmits<{
  archive: [alertId: string];
  reactivate: [alertId: string];
}>();

const { getAlertTag, getAlertDescription } = useAlerts();
const isActive = computed(() => props.alert.status === AlertStatus.ACTIVE);
const isArchived = computed(() => props.alert.status === AlertStatus.ARCHIVED);
const isResolved = computed(() => props.alert.status === AlertStatus.RESOLVED);
const tag = computed(() => getAlertTag(props.alert.type));

const description = computed(() =>
  getAlertDescription(
    props.alert.type,
    {
      startDate: props.alert.metadata.startDate,
      nbAttendees: props.alert.metadata.nbAttendees,
      attendeeName: props.alert.metadata.attendeeName,
    },
    props.scope,
  ),
);
</script>

<template>
  <Card bordered>
    <template #body>
      <div class="flex flex-col gap-3">
        <!-- Header: title + tag -->
        <div class="flex items-center justify-between">
          <span class="body_bold text-primary-color">{{ alert.title }}</span>
          <Tag v-if="tag" :tag="tag" />
        </div>

        <!-- Body: description + metadata -->
        <div class="flex flex-col gap-2">
          <span class="body_regular text-secondary-color">{{ description }}</span>

          <div v-if="isArchived" class="flex items-center gap-1 text-grey-medium-color">
            <SvgIcon name="archive" class="w-[16px] h-[16px] shrink-0" />
            <span v-if="alert.metadata.archivedBy" class="body_regular"
              >Archivée par {{ alert.metadata.archivedBy }} · {{ alert.metadata.archivedAt }}</span
            >
            <span v-else class="body_regular">Archivée · {{ alert.metadata.archivedAt }}</span>
          </div>

          <div v-if="isResolved" class="flex items-center gap-1 text-primary-green-color">
            <SvgIcon name="validate" class="w-[16px] h-[16px] shrink-0" />
            <span class="body_regular">Résolue automatiquement · {{ alert.metadata.resolvedAt }}</span>
          </div>
        </div>

        <!-- Footer: actions -->
        <div class="flex items-center justify-between">
          <button
            v-if="link"
            class="button_secondary_default flex items-center gap-1"
            @click="navigateTo(link, { external: true, open: { target: '_blank' } })"
          >
            <SvgIcon name="link" class="w-[16px] h-[16px]" />
            <span class="body_regular">Voir la session</span>
          </button>
          <span v-else></span>

          <Tooltip v-if="isActive && alert.metadata.canArchive !== false" position="top-left" minified append-to-body>
            <template #img>
              <SvgButton name="archive" variant="grey" @click="emit('archive', alert.id)" />
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">Archiver l'alerte</span>
              </div>
            </template>
          </Tooltip>

          <Tooltip
            v-if="isArchived && alert.metadata.canReactivate !== false"
            position="top-left"
            minified
            append-to-body
          >
            <template #img>
              <SvgButton name="undo" variant="grey" @click="emit('reactivate', alert.id)" />
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">Ré-activer l'alerte</span>
              </div>
            </template>
          </Tooltip>
        </div>
      </div>
    </template>
  </Card>
</template>
